/**
 * Feedback Management Modal Component
 *
 * Allows admins to view, approve, reject, and delete feedback for course groups.
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { media } from '../../styles/theme';
import {
  listFeedback,
  approveFeedback,
  rejectFeedback,
  deleteFeedback,
  updateFeedback,
  getImageUploadUrls,
  uploadFeedbackImage,
  getFeedbackImageUrl,
  FEEDBACK_TYPES,
  APPROVAL_STATUS
} from '../../api/feedbackPortalApi';
import { getTermLabel } from '../../api/courseGroupPortalApi';
import FeedbackImageGallery from './FeedbackImageGallery';
import FeedbackImageUpload from './FeedbackImageUpload';

// Styled components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);

  ${media.phone} {
    width: 100%;
    max-width: 100%;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 12px 12px 0 0;

  h3 {
    margin: 0;
    font-size: 18px;
    color: white;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: white;
  line-height: 1;
  padding: 0;

  &:hover {
    opacity: 0.8;
  }
`;

const ModalContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FilterTab = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.$active ? '#f59e0b' : '#d1d5db'};
  border-radius: 20px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  background: ${props => props.$active ? '#fef3c7' : 'white'};
  color: ${props => props.$active ? '#92400e' : '#6b7280'};
  transition: all 0.2s;

  &:hover {
    border-color: #f59e0b;
  }

  .count {
    background: ${props => props.$active ? '#f59e0b' : '#e5e7eb'};
    color: ${props => props.$active ? 'white' : '#374151'};
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    margin-left: 6px;
  }
`;

const FeedbackCard = styled.div`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FeedbackHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
`;

const BadgeGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Badge = styled.span`
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$bg || '#dbeafe'};
  color: ${props => props.$color || '#1e40af'};
`;

const TypeBadge = styled(Badge)`
  background: ${props => props.$type === 'group' ? '#dbeafe' : '#ede9fe'};
  color: ${props => props.$type === 'group' ? '#1e40af' : '#6b21a8'};
`;

const StatusBadge = styled(Badge)`
  background: ${props => {
    switch(props.$status) {
      case 'pending': return '#fef3c7';
      case 'approved': return '#dcfce7';
      case 'rejected': return '#fee2e2';
      default: return '#e5e7eb';
    }
  }};
  color: ${props => {
    switch(props.$status) {
      case 'pending': return '#92400e';
      case 'approved': return '#166534';
      case 'rejected': return '#dc2626';
      default: return '#374151';
    }
  }};
`;

const WeekBadge = styled(Badge)`
  background: #e5e7eb;
  color: #374151;
`;

const FeedbackMeta = styled.div`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;

  .teacher {
    font-weight: 500;
    color: #374151;
  }

  .student {
    margin-top: 4px;
    font-style: italic;
  }
`;

const FeedbackContent = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  white-space: pre-wrap;
`;

const RejectionBox = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  font-size: 13px;
  color: #dc2626;

  strong {
    display: block;
    margin-bottom: 4px;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 6px 14px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$bg || '#3b82f6'};
  color: white;
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ApproveButton = styled(ActionButton)`
  background: #10b981;
`;

const RejectButton = styled(ActionButton)`
  background: #f59e0b;
`;

const DeleteButton = styled(ActionButton)`
  background: #ef4444;
`;

const EditButton = styled(ActionButton)`
  background: #3b82f6;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 32px;
  color: #6b7280;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 8px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 32px;
  color: #6b7280;
`;

const ErrorState = styled.div`
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 6px;
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 16px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  background: ${props => props.$color || '#6b7280'};
  color: white;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Nested modal for rejection reason
const RejectModalOverlay = styled(ModalOverlay)`
  z-index: 1001;
`;

const RejectModal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const RejectModalHeader = styled(ModalHeader)`
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
`;

const RejectModalContent = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 14px;
    font-weight: 500;
    color: #374151;

    span {
      color: #ef4444;
    }
  }
`;

const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }
`;

const EditRow = styled.div`
  display: flex;
  gap: 16px;

  ${FormGroup} {
    flex: 1;
  }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;
  background: white;

  &:focus {
    outline: none;
    border-color: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }
`;

const NumberInput = styled.input`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #f59e0b;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }
`;

const RejectModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;
`;

// Edit modal styled components
const EditModalOverlay = styled(ModalOverlay)`
  z-index: 1001;
`;

const EditModal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 650px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

const EditModalHeader = styled(ModalHeader)`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
`;

const EditModalContent = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const EditModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;
`;

// Helper function to format date
const formatDate = (isoString) => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const FeedbackManagementModal = ({
  show,
  onClose,
  courseGroup,
  onUpdate
}) => {
  // Data state
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Filter state
  const [activeFilter, setActiveFilter] = useState('all');

  // Rejection modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingFeedback, setRejectingFeedback] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTerm, setEditTerm] = useState(1);
  const [editWeek, setEditWeek] = useState(1);
  const [existingImages, setExistingImages] = useState([]);
  const [newEditImages, setNewEditImages] = useState([]);

  // Load feedbacks
  const loadFeedbacks = useCallback(async () => {
    if (!courseGroup?.id) return;

    setLoading(true);
    setError(null);

    try {
      const data = await listFeedback(courseGroup.id);
      setFeedbacks(data);
    } catch (err) {
      console.error('Error loading feedbacks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [courseGroup?.id]);

  useEffect(() => {
    if (show) {
      loadFeedbacks();
      setActiveFilter('all');
    }
  }, [show, loadFeedbacks]);

  // Filter feedbacks
  const filteredFeedbacks = feedbacks.filter(fb => {
    if (activeFilter === 'all') return true;
    return fb.approvalStatus === activeFilter;
  });

  // Count feedbacks by status
  const counts = {
    all: feedbacks.length,
    pending: feedbacks.filter(fb => fb.approvalStatus === APPROVAL_STATUS.PENDING).length,
    approved: feedbacks.filter(fb => fb.approvalStatus === APPROVAL_STATUS.APPROVED).length,
    rejected: feedbacks.filter(fb => fb.approvalStatus === APPROVAL_STATUS.REJECTED).length
  };

  // Handle approve
  const handleApprove = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to approve this feedback?')) return;

    setActionLoading(true);
    try {
      await approveFeedback(feedbackId);
      await loadFeedbacks();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error approving feedback:', err);
      alert(`Failed to approve feedback: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Open reject modal
  const handleReject = (feedback) => {
    setRejectingFeedback(feedback);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  // Close reject modal
  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectingFeedback(null);
    setRejectionReason('');
  };

  // Confirm rejection
  const handleRejectConfirm = async () => {
    if (!rejectingFeedback || !rejectionReason.trim()) return;

    setActionLoading(true);
    try {
      await rejectFeedback(rejectingFeedback.id, rejectionReason.trim());
      closeRejectModal();
      await loadFeedbacks();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error rejecting feedback:', err);
      alert(`Failed to reject feedback: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (feedback) => {
    const typeLabel = feedback.type === FEEDBACK_TYPES.GROUP ? 'group' : 'individual';
    const studentInfo = feedback.studentName ? ` for ${feedback.studentName}` : '';
    const confirmMsg = `Are you sure you want to delete this ${typeLabel} feedback${studentInfo}?`;

    if (!window.confirm(confirmMsg)) return;

    setActionLoading(true);
    try {
      await deleteFeedback(feedback.id);
      await loadFeedbacks();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error deleting feedback:', err);
      alert(`Failed to delete feedback: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Open edit modal
  const handleEdit = (feedback) => {
    setEditingFeedback(feedback);
    setEditContent(feedback.content);
    setEditTerm(feedback.term);
    setEditWeek(feedback.week);
    setExistingImages(feedback.images || []);
    setNewEditImages([]);
    setShowEditModal(true);
  };

  // Close edit modal
  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingFeedback(null);
    setEditContent('');
    setEditTerm(1);
    setEditWeek(1);
    setExistingImages([]);
    setNewEditImages([]);
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  // Confirm edit
  const handleEditConfirm = async () => {
    if (!editingFeedback || !editContent.trim()) return;

    setActionLoading(true);
    try {
      let allImages = [...existingImages];

      // Upload new images if any
      if (newEditImages.length > 0) {
        const imageMetadata = newEditImages.map(img => ({
          fileName: img.file.name,
          contentType: img.file.type
        }));

        const uploadUrls = await getImageUploadUrls(courseGroup.id, editingFeedback.id, imageMetadata);

        await Promise.all(uploadUrls.map((urlInfo, i) =>
          uploadFeedbackImage(urlInfo.uploadUrl, newEditImages[i].file, urlInfo.contentType)
        ));

        const newImagesData = uploadUrls.map((urlInfo, i) => ({
          s3Key: urlInfo.s3Key,
          name: newEditImages[i].name || newEditImages[i].file.name
        }));

        allImages = [...allImages, ...newImagesData];
      }

      await updateFeedback(editingFeedback.id, {
        content: editContent.trim(),
        term: editTerm,
        week: editWeek,
        images: allImages
      });

      closeEditModal();
      await loadFeedbacks();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error updating feedback:', err);
      alert(`Failed to update feedback: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (!show) return null;

  const courseGroupName = courseGroup ? `${courseGroup.title} Y${courseGroup.year}T${courseGroup.term}` : '';

  return (
    <ModalOverlay>
      <Modal>
        <ModalHeader>
          <h3>Feedbacks - {courseGroupName}</h3>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalContent>
          {error && <ErrorState>{error}</ErrorState>}

          {loading ? (
            <LoadingState>Loading feedbacks...</LoadingState>
          ) : (
            <>
              {/* Filter Tabs */}
              <FilterTabs>
                <FilterTab
                  $active={activeFilter === 'all'}
                  onClick={() => setActiveFilter('all')}
                >
                  All
                  <span className="count">{counts.all}</span>
                </FilterTab>
                <FilterTab
                  $active={activeFilter === 'pending'}
                  onClick={() => setActiveFilter('pending')}
                >
                  Pending
                  <span className="count">{counts.pending}</span>
                </FilterTab>
                <FilterTab
                  $active={activeFilter === 'approved'}
                  onClick={() => setActiveFilter('approved')}
                >
                  Approved
                  <span className="count">{counts.approved}</span>
                </FilterTab>
                <FilterTab
                  $active={activeFilter === 'rejected'}
                  onClick={() => setActiveFilter('rejected')}
                >
                  Rejected
                  <span className="count">{counts.rejected}</span>
                </FilterTab>
              </FilterTabs>

              {/* Feedback List */}
              {filteredFeedbacks.length === 0 ? (
                <EmptyState>
                  {activeFilter === 'all'
                    ? 'No feedback has been created for this course group yet.'
                    : `No ${activeFilter} feedback found.`}
                </EmptyState>
              ) : (
                filteredFeedbacks.map(feedback => (
                  <FeedbackCard key={feedback.id}>
                    <FeedbackHeader>
                      <BadgeGroup>
                        <TypeBadge $type={feedback.type}>
                          {feedback.type === FEEDBACK_TYPES.GROUP ? 'Group' : 'Individual'}
                        </TypeBadge>
                        <StatusBadge $status={feedback.approvalStatus}>
                          {feedback.approvalStatus === APPROVAL_STATUS.PENDING && 'Pending'}
                          {feedback.approvalStatus === APPROVAL_STATUS.APPROVED && 'Approved'}
                          {feedback.approvalStatus === APPROVAL_STATUS.REJECTED && 'Rejected'}
                        </StatusBadge>
                      </BadgeGroup>
                      <WeekBadge>
                        {getTermLabel(feedback.term) ? `${getTermLabel(feedback.term)} · Week ${feedback.week}` : `Week ${feedback.week}`}
                      </WeekBadge>
                    </FeedbackHeader>

                    <FeedbackMeta>
                      <div className="teacher">
                        Teacher: {feedback.teacherName || 'Unknown'}
                        <span style={{ fontWeight: 'normal', marginLeft: '16px' }}>
                          {formatDate(feedback.createdAt)}
                        </span>
                      </div>
                      {feedback.type === FEEDBACK_TYPES.INDIVIDUAL && feedback.studentName && (
                        <div className="student">
                          Student: {feedback.studentName}
                        </div>
                      )}
                    </FeedbackMeta>

                    <FeedbackContent>
                      {feedback.content}
                    </FeedbackContent>

                    {feedback.images && feedback.images.length > 0 && (
                      <FeedbackImageGallery images={feedback.images} />
                    )}

                    {feedback.approvalStatus === APPROVAL_STATUS.REJECTED && feedback.rejectionReason && (
                      <RejectionBox>
                        <strong>Rejection Reason:</strong>
                        {feedback.rejectionReason}
                      </RejectionBox>
                    )}

                    <ActionButtons>
                      <EditButton
                        onClick={() => handleEdit(feedback)}
                        disabled={actionLoading}
                      >
                        Edit
                      </EditButton>
                      {feedback.approvalStatus === APPROVAL_STATUS.PENDING && (
                        <>
                          <ApproveButton
                            onClick={() => handleApprove(feedback.id)}
                            disabled={actionLoading}
                          >
                            Approve
                          </ApproveButton>
                          <RejectButton
                            onClick={() => handleReject(feedback)}
                            disabled={actionLoading}
                          >
                            Reject
                          </RejectButton>
                        </>
                      )}
                      <DeleteButton
                        onClick={() => handleDelete(feedback)}
                        disabled={actionLoading}
                      >
                        Delete
                      </DeleteButton>
                    </ActionButtons>
                  </FeedbackCard>
                ))
              )}
            </>
          )}
        </ModalContent>

        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <RejectModalOverlay>
          <RejectModal>
            <RejectModalHeader>
              <h3>Reject Feedback</h3>
              <CloseButton onClick={closeRejectModal}>&times;</CloseButton>
            </RejectModalHeader>

            <RejectModalContent>
              <FormGroup>
                <label>
                  Rejection Reason <span>*</span>
                </label>
                <TextArea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Please provide a reason for rejection so the teacher can revise..."
                  rows={4}
                />
              </FormGroup>
            </RejectModalContent>

            <RejectModalFooter>
              <Button $color="#6b7280" onClick={closeRejectModal}>
                Cancel
              </Button>
              <Button
                $color="#ef4444"
                onClick={handleRejectConfirm}
                disabled={actionLoading || !rejectionReason.trim()}
              >
                {actionLoading ? 'Rejecting...' : 'Reject'}
              </Button>
            </RejectModalFooter>
          </RejectModal>
        </RejectModalOverlay>
      )}

      {/* Edit Feedback Modal */}
      {showEditModal && (
        <EditModalOverlay>
          <EditModal>
            <EditModalHeader>
              <h3>Edit Feedback</h3>
              <CloseButton onClick={closeEditModal}>&times;</CloseButton>
            </EditModalHeader>

            <EditModalContent>
              <EditRow>
                <FormGroup>
                  <label>Term</label>
                  <Select
                    value={editTerm}
                    onChange={(e) => setEditTerm(parseInt(e.target.value))}
                  >
                    <option value={1}>Term 1</option>
                    <option value={2}>Term 2</option>
                    <option value={3}>Term 3</option>
                    <option value={4}>Term 4</option>
                    <option value={0}>Special Term</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <label>Week</label>
                  <NumberInput
                    type="number"
                    min="1"
                    max="12"
                    value={editWeek}
                    onChange={(e) => setEditWeek(parseInt(e.target.value) || 1)}
                  />
                </FormGroup>
              </EditRow>

              <FormGroup>
                <label>
                  Feedback Content <span>*</span>
                </label>
                <TextArea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  placeholder="Edit feedback content..."
                  rows={6}
                />
              </FormGroup>

              {existingImages.length > 0 && (
                <FormGroup>
                  <label>Current Images</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {existingImages.map((img, i) => (
                      <div key={i} style={{ position: 'relative', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', width: '100px' }}>
                        <img src={getFeedbackImageUrl(img.s3Key)} alt={img.name} style={{ width: '100%', height: '80px', objectFit: 'cover', display: 'block' }} />
                        <button
                          onClick={() => handleRemoveExistingImage(i)}
                          disabled={actionLoading}
                          style={{
                            position: 'absolute', top: '2px', right: '2px',
                            width: '20px', height: '20px', borderRadius: '50%',
                            border: 'none', background: 'rgba(239,68,68,0.9)',
                            color: 'white', fontSize: '12px', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0
                          }}
                        >&times;</button>
                        <div style={{ fontSize: '11px', color: '#6b7280', padding: '3px 4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{img.name}</div>
                      </div>
                    ))}
                  </div>
                </FormGroup>
              )}

              {existingImages.length < 10 && (
                <FormGroup>
                  <label>Add New Images</label>
                  <FeedbackImageUpload
                    images={newEditImages}
                    onChange={setNewEditImages}
                    maxImages={10 - existingImages.length}
                    disabled={actionLoading}
                  />
                </FormGroup>
              )}
            </EditModalContent>

            <EditModalFooter>
              <Button $color="#6b7280" onClick={closeEditModal}>
                Cancel
              </Button>
              <Button
                $color="#3b82f6"
                onClick={handleEditConfirm}
                disabled={actionLoading || !editContent.trim()}
              >
                {actionLoading ? 'Saving...' : 'Save'}
              </Button>
            </EditModalFooter>
          </EditModal>
        </EditModalOverlay>
      )}
    </ModalOverlay>
  );
};

export default FeedbackManagementModal;
